import React from "react";

const TROOP_ICON_BY_ID = {
  warplane: "/battle-sim-icons/warplane.png",
};

const formatTierLabelFallback = (tierValue) => {
  const raw = String(tierValue ?? "").trim();
  if (!raw) return "Tier";
  const phaseMatch = raw.match(/^(\d+):(\d+)$/);
  if (phaseMatch) {
    return `Phase ${phaseMatch[1]} Lv ${phaseMatch[2]}`;
  }
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber)) return `T${asNumber}`;
  return raw;
};

const formatLevelLabelFallback = (levelValue) => {
  const parsed = Number(levelValue);
  return Number.isFinite(parsed) ? `Level ${parsed + 1}` : `Level ${levelValue}`;
};

const TroopInputList = ({
  troopDefinitions,
  values,
  onChange,
  tierByTroop = {},
  tierOptionsByTroop = {},
  tierOptionLabelsByTroop = {},
  levelByTroop = {},
  levelOptionsByTroop = {},
  levelOptionLabelsByTroop = {},
  onTierChange,
  onLevelChange,
  selectedTierMetaByTroop = {},
  rowOrder = ["front", "mid", "back", "special"],
  rowLabels = {},
}) => {
  return (
    <div className="army-troop-list">
      {rowOrder.map((rowKey) => {
        const rowTroops = troopDefinitions
          .filter((troop) => troop.row === rowKey)
          .sort((a, b) => Number(a.orderInRow ?? 0) - Number(b.orderInRow ?? 0));
        if (!rowTroops.length) return null;

        return (
          <div className={`army-row-group army-row-group-${rowKey}`} key={rowKey}>
            <div className="army-row-title">{rowLabels[rowKey] || rowKey}</div>
            <div className="army-row-grid">
              {rowTroops.map((troop) => {
                const iconSrc = troop.iconPath || TROOP_ICON_BY_ID[troop.id] || "";
                const tierOptions = tierOptionsByTroop[troop.id] || [];
                const levelOptions = levelOptionsByTroop[troop.id] || [];
                const hasTierOptions = tierOptions.length > 0;
                const hasLevelOptions = levelOptions.length > 0;
                const isAutoFilled = Boolean(troop.isAutoFilledFromLimit);
                const selectedMeta = selectedTierMetaByTroop[troop.id] || {};
                const specialSkillLabel = String(selectedMeta?.specialSkill?.label || "").trim();
                const specialSkillDescription = String(selectedMeta?.specialSkill?.description || "").trim();
                return (
                  <label
                    className={`army-input-row row-${rowKey} ${hasTierOptions ? "has-tier" : ""} ${hasLevelOptions ? "has-level" : ""}`}
                    key={troop.id}
                  >
                    <span className="army-input-label-wrap">
                      {iconSrc ? (
                        <img src={iconSrc} alt={`${troop.label} icon`} className="army-troop-icon" />
                      ) : null}
                      <span className="army-input-labels">
                        <strong>{troop.label}</strong>
                        <em>{troop.exampleTierName}</em>
                        <small>{troop.battlefieldRole}</small>
                        {specialSkillLabel ? (
                          <small
                            className="army-troop-special-skill"
                            title={specialSkillDescription || specialSkillLabel}
                          >
                            Skill: {specialSkillLabel}
                          </small>
                        ) : null}
                      </span>
                    </span>
                    {troop.inputType === "checkbox" ? (
                      <input
                        type="checkbox"
                        className="army-toggle-input"
                        checked={Number(values[troop.id] || 0) > 0}
                        onChange={(event) => onChange(troop.id, event.target.checked ? 1 : 0)}
                      />
                    ) : (
                      <input
                        type="text"
                        inputMode="numeric"
                        value={Number(values[troop.id] ?? 0).toLocaleString()}
                        onChange={(event) => onChange(troop.id, event.target.value)}
                        disabled={isAutoFilled}
                        title={isAutoFilled ? "Controlled by Zombie Limit" : ""}
                      />
                    )}
                    {hasTierOptions ? (
                      <select
                        className="army-tier-select"
                        value={String(tierByTroop[troop.id] ?? "")}
                        onChange={(event) => onTierChange?.(troop.id, event.target.value)}
                      >
                        {tierOptions.map((tier) => (
                          <option key={`${troop.id}-${tier}`} value={String(tier)}>
                            {tierOptionLabelsByTroop[troop.id]?.[String(tier)] || formatTierLabelFallback(tier)}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    {hasLevelOptions ? (
                      <select
                        className="army-level-select"
                        value={String(levelByTroop[troop.id] ?? "")}
                        onChange={(event) => onLevelChange?.(troop.id, event.target.value)}
                      >
                        {levelOptions.map((level) => (
                          <option key={`${troop.id}-L${level}`} value={String(level)}>
                            {levelOptionLabelsByTroop[troop.id]?.[String(level)] || formatLevelLabelFallback(level)}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TroopInputList;
